export interface Product {
  slug: string;
  title: string;
  desc: string;
  sizes: string;
  categoryId: string;
  categoryLabel: string;
  metaTitle: string;
  metaDesc: string;
  ogImage: string;
}

/** Category → OG image URL mapping */
export const categoryOgImages: Record<string, string> = {
  pipes: "https://txsbawikklpyjvvaiivi.supabase.co/storage/v1/object/public/og-images/og-pipes-tubes.jpg",
  valves: "https://txsbawikklpyjvvaiivi.supabase.co/storage/v1/object/public/og-images/og-valves.jpg",
  fittings: "https://txsbawikklpyjvvaiivi.supabase.co/storage/v1/object/public/og-images/og-fittings-flanges.jpg",
  structural: "https://txsbawikklpyjvvaiivi.supabase.co/storage/v1/object/public/og-images/og-structural.jpg",
  fire: "https://txsbawikklpyjvvaiivi.supabase.co/storage/v1/object/public/og-images/og-fire-safety.jpg",
};

export interface Category {
  id: string;
  label: string;
  products: Product[];
}

const raw = [
  {
    id: "pipes",
    label: "Pipes & Tubes",
    products: [
      { slug: "ms-round-pipes", title: "MS Round Pipes", desc: "Mild Steel round pipes in various diameters and thicknesses for structural and general engineering use.", sizes: "15mm – 600mm NB" },
      { slug: "ms-square-pipes", title: "MS Square Pipes", desc: "Square hollow sections ideal for fabrication, frames, furniture, and structural applications.", sizes: "20×20mm – 200×200mm" },
      { slug: "ms-rectangular-pipes", title: "MS Rectangular Pipes", desc: "Rectangular hollow sections for construction, machine frames, and architectural works.", sizes: "20×40mm – 200×400mm" },
      { slug: "gi-pipes-tubes", title: "GI Pipes & Tubes", desc: "Hot-dip galvanized iron pipes for plumbing, water supply, and corrosion-resistant applications.", sizes: "15mm – 150mm NB" },
      { slug: "erw-steel-tubes", title: "ERW Steel Tubes", desc: "Black & galvanized ERW steel tubes conforming to IS:1239 & BS:1387, available in Light, Medium, and Heavy series.", sizes: "½\" – 350mm NB" },
      { slug: "seamless-pipes-tubes", title: "Seamless Pipes & Tubes", desc: "Maharashtra Seamless boiler tubes and pipes for high-pressure and precision applications.", sizes: "6mm – 500mm NB" },
      { slug: "stainless-steel-pipes", title: "Stainless Steel Pipes", desc: "SS pipes in Grade 304, 202, and 316 — including polish pipes, square pipes, and NB pipes as per ANSI B36.19.", sizes: "⅛\" – 14\" (3mm – 350mm)" },
      { slug: "gp-pipes-hollow-sections", title: "GP Pipes & RHS/SHS Hollow Sections", desc: "Nezone & APL Apollo branded GP pipes and rectangular/square hollow section pipes.", sizes: "As per manufacturer specs" },
    ],
  },
  {
    id: "valves",
    label: "Valves",
    products: [
      { slug: "zoloto-valves", title: "Zoloto Valves", desc: "Brass ball valves, GM horizontal valves, safety valves, CI globe, butterfly, sluice valves, PRV, and more.", sizes: "15mm – 300mm" },
      { slug: "kartar-fire-safety-valves", title: "Kartar Fire Safety Valves", desc: "Butterfly valves, hydrant valves, sprinklers, hose boxes, fire hose reel drums, and fire couplings.", sizes: "50mm – 300mm" },
      { slug: "kartar-ball-check-valves", title: "Kartar Ball & Check Valves", desc: "GM ball valves, CP ball valves, cast iron ball valves, flanged ball valves, butterfly slimline valves, and strainers.", sizes: "15mm – 300mm" },
      { slug: "normex-valves", title: "Normex Valves", desc: "Ball check valves (flanged & threaded), silent check valves, rubber lined check valves, wafer check valves, and butterfly valves.", sizes: "50mm – 600mm" },
      { slug: "racer-valves", title: "Racer Valves", desc: "IC ball valves, butterfly valves, gate valves, Y strainers, wafer check valves, needle valves, and PP ball valves.", sizes: "15mm – 300mm" },
      { slug: "matrix-valves", title: "Matrix (MV) Valves", desc: "Brass ball valves, gas valves, mini valves, check valves, bib cocks, three-way ball valves, SS ball valves, and teflon tape.", sizes: "15mm – 100mm" },
      { slug: "kitz-valves", title: "Kitz Valves", desc: "Butterfly XJ valves, super Z brass valves, and solder joint ends design.", sizes: "As per Kitz standards" },
    ],
  },
  {
    id: "fittings",
    label: "Fittings & Flanges",
    products: [
      { slug: "threaded-pipe-fittings", title: "Threaded Pipe Fittings (VS)", desc: "Elbows (90° & 45°), tees, crosses, unions, couplings, hex nipples, plugs, reducing bushes — up to 3000 LBS.", sizes: "¼\" – 4\"" },
      { slug: "forged-socket-weld-fittings", title: "Forged Socket Weld Fittings (VS)", desc: "Socket weld elbows, tees, crosses, unions, couplings — available in 3000, 6000 & 9000 LBS ratings.", sizes: "¼\" – 4\"" },
      { slug: "seamless-butt-weld-fittings", title: "Seamless Butt Weld Fittings", desc: "Seamless tees, reducers, reducing tees, short bends, and long bends for pipeline construction.", sizes: "½\" – 20\"" },
      { slug: "ms-gi-flanges", title: "MS & GI Flanges", desc: "Mild steel and galvanized iron flanges for piping connections in various pressure ratings.", sizes: "15mm – 300mm" },
      { slug: "ss-flanges-bolts-nuts", title: "SS Flanges, Bolts & Nuts", desc: "Stainless steel flanges, bolt-nut sets, and short/long bends for SS piping systems.", sizes: "As per ANSI standards" },
      { slug: "neoprene-rubber-bellows", title: "Neoprene Rubber Bellows", desc: "Kartar neoprene rubber bellows with control unit for vibration isolation and thermal expansion.", sizes: "50mm – 300mm" },
    ],
  },
  {
    id: "structural",
    label: "Structural & More",
    products: [
      { slug: "ms-angles-channels", title: "MS Angles & Channels", desc: "Mild steel angles and channels for heavy-duty construction, infrastructure, and fabrication projects.", sizes: "As per IS standards" },
      { slug: "ismb-beams", title: "ISMB Beams", desc: "Indian Standard Medium Weight Beams for structural framing and load-bearing applications.", sizes: "As per IS standards" },
      { slug: "bends-ms-gi-ss", title: "Bends (MS, GI & SS)", desc: "Short bends and long bends in MS, GI, and SS for pipeline direction changes.", sizes: "15mm – 300mm" },
      { slug: "bolt-nuts-fasteners", title: "Bolt Nuts & Fasteners", desc: "MS, GI, and SS bolt-nut sets, rawl anchor bolts, pin type anchor fasteners for secure connections.", sizes: "Various sizes" },
      { slug: "thread-rods-polish-rods", title: "Thread Rods & Polish Rods", desc: "GI thread rods and MS polish rods for various industrial and construction applications.", sizes: "Standard lengths" },
      { slug: "gaskets-pressure-gauges", title: "Gaskets & Pressure Gauges", desc: "Metallic & non-metallic gaskets and pressure gauges for pipeline and equipment monitoring.", sizes: "As per requirement" },
      { slug: "welding-electrodes", title: "Welding Electrodes", desc: "Best Arc, Superon, and ESAB welding electrodes for all welding requirements.", sizes: "2.50×350, 3.15×350, 4.00×450 mm" },
      { slug: "water-meters", title: "Water Meters", desc: "Besto & Kranti water meters — including flange end models for commercial and industrial use.", sizes: "15mm – 150mm" },
    ],
  },
  {
    id: "fire",
    label: "Fire & Safety",
    products: [
      { slug: "fire-detection-alarm-systems", title: "Fire Detection & Alarm Systems", desc: "Automatic fire detection and alarm systems for commercial and industrial buildings.", sizes: "Project-based" },
      { slug: "fire-sprinkler-systems-bms", title: "Fire Sprinkler Systems & BMS", desc: "Automatic fire sprinkler systems with building management system integration.", sizes: "Project-based" },
      { slug: "fire-hydrant-down-comer-systems", title: "Fire Hydrant & Down Comer Systems", desc: "Complete fire hydrant systems with single/double head hydrant valves and down comer installations.", sizes: "As per NBC standards" },
      { slug: "fire-extinguishers", title: "Fire Extinguishers", desc: "New ISI mark fire extinguishers and professional refilling services for all types.", sizes: "1 kg – 75 kg" },
      { slug: "hose-boxes-fire-hoses", title: "Hose Boxes & Fire Hoses", desc: "Single/double door hose boxes, fire hose reel drums, GM & SS RRL hoses, and fire hose couplings.", sizes: "As per NBC standards" },
      { slug: "personal-protective-equipment", title: "Personal Protective Equipment", desc: "Complete range of PPE and fire safety equipment for industrial safety compliance.", sizes: "All standard sizes" },
    ],
  },
];

/**
 * Hand-written, high-intent SEO copy for the MS & GI pages.
 * Titles ≤ 60 chars, descriptions ≤ 158 chars, each unique.
 */
const seoOverrides: Record<string, { metaTitle: string; metaDesc: string }> = {
  "ms-round-pipes": {
    metaTitle: "MS Round Pipe Dealer Coimbatore | Price & Sizes",
    metaDesc:
      "Buy MS round pipes 15mm–600mm NB in Coimbatore. Jindal, Tata & APL Apollo stock, IS:1239 grades, cut-to-length & same-day bulk dispatch. Get today's price.",
  },
  "ms-square-pipes": {
    metaTitle: "MS Square Pipe Supplier Coimbatore | 20–200mm",
    metaDesc:
      "MS square hollow sections 20×20mm to 200×200mm for fabrication, frames & structural work. Trusted Coimbatore stockist since 1990. Request a bulk quote today.",
  },
  "ms-rectangular-pipes": {
    metaTitle: "MS Rectangular Pipe Dealer Coimbatore | RHS",
    metaDesc:
      "MS rectangular hollow sections 20×40mm to 200×400mm for construction & machine frames. APL Apollo and Tata RHS in stock in Coimbatore. Ask for today's rate.",
  },
  "gi-pipes-tubes": {
    metaTitle: "GI Pipe Dealer Coimbatore | 15mm–150mm Price",
    metaDesc:
      "Hot-dip galvanized GI pipes 15mm–150mm NB for plumbing & water supply. IS:1239 Light, Medium & Heavy class from an authorized Coimbatore dealer. Get a quote.",
  },
  "erw-steel-tubes": {
    metaTitle: "ERW Steel Tube Supplier Coimbatore | IS:1239",
    metaDesc:
      "Black & GI ERW steel tubes to IS:1239 and BS:1387 in Light, Medium and Heavy series, ½\" to 350mm NB. Bulk supply from Coimbatore across South India.",
  },
  "gp-pipes-hollow-sections": {
    metaTitle: "GP Pipes & RHS/SHS Supplier Coimbatore",
    metaDesc:
      "Nezone & APL Apollo GP pipes plus square and rectangular hollow sections for roofing, sheds and structures. Coimbatore stockist with pan-India dispatch.",
  },
  "ms-gi-flanges": {
    metaTitle: "MS & GI Flange Supplier Coimbatore | 15–300mm",
    metaDesc:
      "MS and galvanized iron flanges 15mm–300mm in slip-on, blind and plate types for all pressure ratings. Ready stock in Coimbatore. Send your flange list.",
  },
  "bends-ms-gi-ss": {
    metaTitle: "MS, GI & SS Bends Coimbatore | Short & Long",
    metaDesc:
      "Short and long bends in MS, GI and stainless steel, 15mm–300mm, for pipeline direction changes. Buy from a Coimbatore pipe fittings dealer since 1990.",
  },
  "ms-angles-channels": {
    metaTitle: "MS Angle & Channel Dealer Coimbatore | IS Sizes",
    metaDesc:
      "MS angles and channels in all IS standard sizes for construction, sheds and fabrication. Bulk tonnage supply from Coimbatore with fast delivery. Get pricing.",
  },
};

export const categories: Category[] = raw.map((cat) => ({
  id: cat.id,
  label: cat.label,
  products: cat.products.map((p) => ({
    ...p,
    categoryId: cat.id,
    categoryLabel: cat.label,
    metaTitle:
      seoOverrides[p.slug]?.metaTitle ?? `${p.title} | Taher Steel Coimbatore`.slice(0, 60),
    metaDesc:
      seoOverrides[p.slug]?.metaDesc ??
      `${p.title} (${p.sizes}) from Taher Steel, Coimbatore. ${p.desc}`.slice(0, 158),
    ogImage: categoryOgImages[cat.id] || "",
  })),
}));


export const allProducts: Product[] = categories.flatMap((c) => c.products);

export const getProductBySlug = (slug: string): Product | undefined =>
  allProducts.find((p) => p.slug === slug);
